/**
 * CreditsBlock — a scene's credits (Rigger / Model / Photographer + Location)
 * for the shared article reader's right rail and mobile aux section (CAS
 * migration plan D8, §2.3). Same rows and `CreditMention` rendering as the
 * legacy scene page's Spec sidebar (`SidebarCredits` in scene-view.tsx, which
 * stays as-is for unconverted scenes); the heading is supplied by the caller.
 *
 * Credits are public attribution: callers render this for every viewer,
 * including locked (gated, no access) ones.
 *
 * No hooks / no 'use client' directive: it renders inside the client
 * ArticleView, and `hasCredits` is a plain predicate safe to call anywhere.
 * Row labels are passed in already localized (ArticleView's translator).
 */
import { CreditMention, type CreditData } from '@/components/content/credit-mention'

export interface SceneCredits {
  model?: CreditData | null
  rigger?: CreditData | null
  photographer?: CreditData | null
  location?: string | null
}

/** Localized row labels (articleView.creditRigger / …). */
export interface CreditLabels {
  rigger: string
  model: string
  photographer: string
  location: string
}

function rowsOf(credits: SceneCredits) {
  return [
    { role: 'rigger' as const, data: credits.rigger },
    { role: 'model' as const, data: credits.model },
    { role: 'photographer' as const, data: credits.photographer },
  ].filter((r) => r.data?.name)
}

/** True when there is at least one named credit or a location to show. */
export function hasCredits(credits: SceneCredits | null | undefined): credits is SceneCredits {
  if (!credits) return false
  return rowsOf(credits).length > 0 || !!credits.location
}

export function CreditsBlock({ credits, labels }: { credits: SceneCredits; labels: CreditLabels }) {
  const rows = rowsOf(credits)
  if (rows.length === 0 && !credits.location) return null
  return (
    <div className="flex flex-col gap-1.5 text-[13px] text-muted-foreground">
      {rows.map(({ role, data }) => (
        <span key={role}>
          {labels[role]} —{' '}
          <CreditMention name={data?.name} credit={data} linkClassName="text-brand-700 hover:opacity-70" />
        </span>
      ))}
      {credits.location && (
        <span>
          {labels.location} — {credits.location}
        </span>
      )}
    </div>
  )
}
